import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type ResearchItem = Database["public"]["Tables"]["research_items"]["Row"];
export type ResearchStatus = Database["public"]["Enums"]["research_status"];
export type ResearchAxis = Database["public"]["Enums"]["research_axis"];
export type TaxonomyValue = Database["public"]["Tables"]["research_taxonomy_values"]["Row"];

export interface ResearchFilters {
  q?: string;
  status?: ResearchStatus | "all";
  axis?: ResearchAxis | "all";
  tagIds?: string[];
}

export function useResearchItems(filters: ResearchFilters = {}) {
  return useQuery({
    queryKey: ["research_items", filters],
    queryFn: async () => {
      let q = supabase
        .from("research_items")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(200);
      if (filters.status && filters.status !== "all") q = q.eq("status", filters.status);
      if (filters.axis && filters.axis !== "all") q = q.eq("primary_axis", filters.axis);
      if (filters.q) q = q.ilike("title", `%${filters.q}%`);
      const { data, error } = await q;
      if (error) throw error;
      let rows = data ?? [];
      if (filters.tagIds && filters.tagIds.length) {
        // Direct tag matches via the join table
        const { data: tagRows } = await supabase
          .from("research_item_tags")
          .select("item_id, taxonomy_value_id")
          .in("taxonomy_value_id", filters.tagIds);
        const ids = new Set((tagRows ?? []).map((r) => r.item_id));

        // KG expansion: if any filter tag is linked to a KG node,
        // also include items connected to that node via the graph.
        const { data: linkedTags } = await (supabase as any)
          .from("research_taxonomy_values")
          .select("id, kg_node_id")
          .in("id", filters.tagIds);
        const kgNodeIds = (linkedTags ?? [])
          .map((t: any) => t.kg_node_id)
          .filter(Boolean) as string[];

        if (kgNodeIds.length) {
          // 1. items that are themselves the linked KG nodes
          const { data: directNodes } = await (supabase as any)
            .from("research_kg_nodes")
            .select("external_id")
            .in("id", kgNodeIds)
            .eq("external_table", "research_items");
          (directNodes ?? []).forEach((n: any) => n.external_id && ids.add(n.external_id));

          // 2. items connected via edges to any of those KG nodes
          const { data: edges } = await (supabase as any)
            .from("research_kg_edges")
            .select("from_node_id, to_node_id")
            .or(
              `from_node_id.in.(${kgNodeIds.join(",")}),to_node_id.in.(${kgNodeIds.join(",")})`
            );
          const otherEnds = new Set<string>();
          (edges ?? []).forEach((e: any) => {
            if (!kgNodeIds.includes(e.from_node_id)) otherEnds.add(e.from_node_id);
            if (!kgNodeIds.includes(e.to_node_id)) otherEnds.add(e.to_node_id);
          });
          if (otherEnds.size) {
            const { data: itemNodes } = await (supabase as any)
              .from("research_kg_nodes")
              .select("external_id")
              .in("id", Array.from(otherEnds))
              .eq("external_table", "research_items");
            (itemNodes ?? []).forEach((n: any) => n.external_id && ids.add(n.external_id));
          }
        }

        rows = rows.filter((r) => ids.has(r.id));
      }

      return rows;
    },
  });
}

export function useResearchItem(id: string | undefined) {
  return useQuery({
    queryKey: ["research_item", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_items")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useResearchItemTags(itemId: string | undefined) {
  return useQuery({
    queryKey: ["research_item_tags", itemId],
    enabled: !!itemId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_item_tags")
        .select("taxonomy_value_id, research_taxonomy_values(*)")
        .eq("item_id", itemId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useTaxonomy() {
  return useQuery({
    queryKey: ["research_taxonomy"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_taxonomy_values")
        .select("*")
        .order("axis")
        .order("sort_order")
        .order("value");
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSaveResearchItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<ResearchItem> & { id?: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      if (!uid) throw new Error("Not authenticated");
      const base = { ...payload, updated_by: uid };
      if (payload.id) {
        const { data, error } = await supabase
          .from("research_items")
          .update(base)
          .eq("id", payload.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase
        .from("research_items")
        .insert({
          title: payload.title || "Untitled",
          ...base,
          author_id: uid,
          created_by: uid,
        } as any)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (item) => {
      qc.invalidateQueries({ queryKey: ["research_items"] });
      if (item?.id) qc.invalidateQueries({ queryKey: ["research_item", item.id] });
    },
  });
}

export function useSetItemTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, tagIds }: { itemId: string; tagIds: string[] }) => {
      await supabase.from("research_item_tags").delete().eq("item_id", itemId);
      if (tagIds.length) {
        await supabase
          .from("research_item_tags")
          .insert(tagIds.map((t) => ({ item_id: itemId, taxonomy_value_id: t })));
      }
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["research_item_tags", v.itemId] });
      qc.invalidateQueries({ queryKey: ["research_items"] });
    },
  });
}

export function useTransitionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ResearchStatus }) => {
      const patch: any = { status };
      if (status === "approved") {
        const { data: u } = await supabase.auth.getUser();
        patch.approved_by = u.user?.id;
        patch.approved_at = new Date().toISOString();
      }
      if (status === "published") patch.published_at = new Date().toISOString();
      if (status === "archived") patch.archived_at = new Date().toISOString();
      const { error } = await supabase.from("research_items").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["research_items"] });
      qc.invalidateQueries({ queryKey: ["research_item", v.id] });
    },
  });
}
